// Generated by scripts/build-rules-content.mjs from glossary.json (aligned 9 Oct 2026).
// Maintained here since: the card "master-data-checks" was added by hand on 13 Oct 2026.
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { Explanation, HowItWorks, WarningOrBlocked } from "../types";

/** The rules that apply in every country. */
export const GLOSSARY_RULES: readonly Explanation[] = [
  {
    id: "scope-inquiries-only",
    title: "Only inquiries are worked on",
    status: "active",
    tone: "info",
    topic: "mail-and-case",
    checks:
      "Whether the mail asks for a price or a quotation. Purchase orders, order status questions, invoices, delivery changes, complaints and other mail are read and counted, but get no customer, lines, prices or draft.",
    why: "In Go Autonomous many failed drafts were mails that were never inquiries, such as thank-you mails, lead-time questions and orders.",
    youSee:
      'Info: purchase orders are listed under "Purchase orders (parked)", all other mail under "Not an inquiry". Their case page shows only the mail.',
    whatToDo:
      'Handle these mails as today. If a parked mail really asks for a quotation, answer it as usual and use "Give feedback", so the sorting gets better.',
    example:
      'A customer sends a purchase order and adds "please also quote 2 spare belts". I2Q keeps it as an inquiry, so the price question is not lost. A plain purchase order goes to "Purchase orders (parked)".',
    registryIds: [],
    glossaryIds: [],
    source: "docs/scope.md; goa-knowledge.md part A section 1 point 3",
  },
  {
    id: "newest-message-only",
    title: "Only the newest message is read",
    status: "active",
    tone: "info",
    topic: "mail-and-case",
    checks:
      "I2Q reads the newest message of a thread and the files attached to it. Older messages are folded away and not turned into lines.",
    why: "Go Autonomous read whole threads and quoted items from old messages and from signatures again.",
    youSee: 'Info: older messages sit under "Show earlier messages" on the case page.',
    whatToDo:
      'If the customer refers to lines in an earlier message ("same as last time"), add those lines yourself.',
    example:
      'A customer replies "OK, and please add 4 pcs of the seal" above a long thread. I2Q quotes the seal only, not every item from the earlier quotation.',
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 1 point 3; part B section 3 (Thread)",
  },
  {
    id: "copies-and-duplicates",
    title: "The same inquiry twice",
    status: "active",
    tone: "info",
    topic: "mail-and-case",
    checks:
      "Whether the mail is a copy of a case that already exists, for example because it reached two mailboxes.",
    why: "Go Autonomous created up to four drafts for one mail, and a forwarded mail came in a second time.",
    youSee: 'Info: a copy shows "Copy of another mail" and is listed under "Related mails".',
    whatToDo: "Work on the original case only.",
    example:
      "A customer writes to the shared mailbox and to the account manager, who forwards it to the same box. Today the exact copy is marked. Finding the forwarded copy is planned.",
    plannedPart:
      "Also find the same inquiry when a colleague forwarded it, by sender, subject and attachment.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 4 (duplicates through forwards, Issue Log #49)",
  },
  {
    id: "customer-finding",
    title: "Finding the customer",
    status: "active",
    tone: "info",
    topic: "customer",
    checks:
      "Which customer account in F&O the mail belongs to. I2Q looks at earlier quotations and orders of the sender, the contacts, a quotation number in the mail, the company domain and the delivery address.",
    why: "Go Autonomous failed most often on the customer, and every price and margin depends on the right account.",
    youSee:
      'Info when certain: the customer name. Warning when not sure: "Suggested, please confirm" with the reason, or "Customer: not identified". Prices appear only once the customer is certain or confirmed.',
    whatToDo:
      'Read the reason (Why). Click "Confirm" with the name on it if it fits, otherwise "Not them" or "Find customer" and search by name or account number.',
    example:
      "Two plants of the same group write from one domain. I2Q suggests the account most often quoted for that domain. You see it is the other plant and pick it.",
    registryIds: [],
    glossaryIds: [],
    source:
      'goa-knowledge.md part A section 1 point 2, section 2.1; help topic "How the customer is found"',
  },
  {
    id: "internal-forwards",
    title: "Mail forwarded by a colleague",
    status: "active",
    tone: "info",
    topic: "customer",
    checks:
      "When a Norican colleague forwards a customer mail, I2Q looks past the colleague to the original customer.",
    why: "Go Autonomous treated the colleague as the customer and even created Norican addresses as customer contacts.",
    youSee: "Info: the customer card shows the original customer, not the colleague.",
    whatToDo:
      "Check that the customer is the one in the forwarded mail. Send the quotation to the customer, not back to the colleague, unless agreed.",
    example:
      "A field service engineer forwards a plant manager's request for wear parts. I2Q finds the plant's account, not the engineer.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.1 (Internal forwards)",
  },
  {
    id: "freemail-senders",
    title: "Senders with a private mail address",
    status: "active",
    tone: "info",
    topic: "customer",
    checks:
      "Mail from addresses such as gmail or outlook.com is not matched by its domain, because the domain says nothing about the company.",
    why: "Many distributors write from private addresses, and in India about a third of the mails where Go Autonomous found no customer came from such addresses.",
    youSee:
      "Info: the customer is found from earlier documents of the exact address, or it is a suggestion for you to confirm.",
    whatToDo:
      "Check the signature and the delivery address in the mail, then confirm or pick the customer.",
    example:
      'A distributor writes from a gmail address for the first time. I2Q cannot be sure and shows "Customer: not identified". You search the distributor by name.',
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.1 (Freemail senders)",
  },
  {
    id: "agents-and-distributors",
    title: "Agents and distributors",
    status: "active",
    tone: "warning",
    topic: "customer",
    checks:
      "Whether the sender writes for an end customer. When an agent, a trader or a distributor names an end customer, I2Q shows both and puts one account first (product owner, 14 October 2026). First an account of the agent for this end customer, then what sales quoted on the earlier mails of the sender, then the country default: India and the UK the agent, every other country the end customer.",
    why: "About 30 % of European customers deal with us through agents, and Go Autonomous could link an agent to only one customer.",
    youSee:
      'Warning: the customer card shows the sender as "Agent" and the end customer next to it. The party quoted first carries "Quoted first", as "Suggested, please confirm" unless the evidence is clear. When no account of that party is found, the open point names the party.',
    whatToDo:
      'Confirm the account with "Quoted first", or pick the other party when the mail asks for it. Send the quotation to the agent, trader or distributor.',
    example:
      "An agent in Poland asks for a price for a foundry customer. I2Q puts the foundry's account first and shows the agent next to it. You confirm it and reply to the agent.",
    plannedPart: "A list of agent mail domains per country, so I2Q knows an agent at once.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.1 (Agents), section 9.3 (Agents, four cases)",
  },
  {
    id: "competitor-domains",
    title: "Mail from a competitor",
    status: "active",
    tone: "blocked",
    topic: "customer",
    checks:
      "Whether the sender, or the original sender behind a forward by a colleague, uses a domain on the competitor list. Norican's own domains are never checked, and private mail addresses such as gmail are not checked against this list.",
    why: "Go Autonomous kept a list of about 430 competitor domains, because Norican quotes competitors higher or not at all.",
    youSee:
      'Blocked in every country: "The sender\'s domain is on the competitor list." The case is held and the queue shows this reason first. Where the competitor is also a customer or supplier of your company (Spain, North America), you see a warning instead.',
    whatToDo: "Handle this inquiry by hand and quote only if your manager agrees.",
    example:
      'A colleague forwards a mail that asks for 20 blast wheel blades. The original sender is a known competitor. The case shows "The original sender\'s domain is on the competitor list."',
    registryIds: ["gate.competitor"],
    glossaryIds: [],
    modes: {
      "gate.competitor": {
        default: "block",
      },
    },
    source:
      "goa-knowledge.md part A section 2.1 (Competitor domains), backlog M3; GoA live disallowed-domain list",
  },
  {
    id: "sanctioned-countries",
    title: "Sanctioned countries",
    status: "active",
    tone: "blocked",
    topic: "customer",
    checks:
      "Whether the sender's mail domain, or the original sender's behind a forward, belongs to a sanctioned country. The list has Russia (.ru), Belarus (.by), Iran (.ir), Syria (.sy), North Korea (.kp), Cuba (.cu), Libya (.ly), Sudan (.sd) and Crimea. Private Russian mail providers count too.",
    why: "Go Autonomous sent these mails to manual handling, and a quotation to a sanctioned party must never be prepared by a tool.",
    youSee:
      'Blocked in every country: "The sender\'s mail domain belongs to Russia, which is on the sanctions list." The case is held and the queue shows this reason first.',
    whatToDo: "Do not quote. Pass the inquiry to your manager for an export control check.",
    example:
      "A mail from a .ru address asks for spare parts for an old machine. The case is held with the sanctions reason, and you pass it to your manager.",
    registryIds: ["gate.sanctioned"],
    glossaryIds: [],
    modes: {
      "gate.sanctioned": {
        default: "block",
      },
    },
    source: "goa-knowledge.md part A section 2.1 (Sanctioned countries), backlog M3",
  },
  {
    id: "portal-customers",
    title: "Customers that use a buying portal",
    status: "active",
    tone: "blocked",
    topic: "customer",
    checks:
      "Whether the customer account, or the domain the mail came from, is on your company's portal list for quotations. One car maker's supplier portal is on the list for every country. A portal that many customers share does not count on its own.",
    why: "Some customers only accept answers through their portal, so a mailed quotation is lost work.",
    youSee:
      'Blocked: "This customer handles quotations through a supplier portal." or "This mail comes from a supplier portal that is not used for quotations by mail." Customers listed for portal orders only are not held on an inquiry. The customer card still shows the buyer, marked "Procurement portal".',
    whatToDo: "Quote this customer through their portal, not by mail.",
    example:
      "A car maker's supplier portal sends a request for quotation by mail. The case is held with the portal reason, and you answer in the portal.",
    registryIds: ["gate.portal"],
    glossaryIds: [],
    modes: {
      "gate.portal": {
        default: "block",
      },
    },
    source: "goa-knowledge.md part A section 2.1 (Portal customers), backlog M4",
  },
  {
    id: "prospect-and-credit-block",
    title: "New prospects and accounts on stop",
    status: "planned",
    tone: "warning",
    topic: "customer",
    checks:
      "Whether the account is a real customer or only a prospect, and whether it is blocked for credit reasons.",
    why: "Go Autonomous could not handle prospects, and it stopped quotations for customers that were only blocked for invoicing.",
    youSee:
      'Warning (planned): "Prospect, not a customer yet" or "Account on stop". A credit block will not stop the quotation, only flag it, because a block stops the order, not the price.',
    whatToDo:
      "For a prospect, follow your country's way to create the customer. For an account on stop, quote if you want, and talk to Finance before an order.",
    example:
      "A foundry asks for a price for the first time. I2Q cannot find an account and flags it as unknown, so you create it in CRM first.",
    registryIds: [],
    glossaryIds: [],
    source:
      "goa-knowledge.md part A section 2.1 (Prospect vs customer, Credit block), section 9.1 (#86, #93)",
  },
  {
    id: "signal-codes",
    title: "Items that should not be quoted",
    status: "active",
    tone: "blocked",
    topic: "items",
    checks:
      "The status code each item has in F&O (F&O calls it the signal code). Items that are blocked, on hold, obsolete or not released are not quoted. Items being phased out are flagged. New, revised and slow items need a check, for example NEW, REV, ECO, NOMOVER or PRO-UPDATE.",
    why: "Go Autonomous quoted phased-out items and failed silently on new and revised ones.",
    youSee:
      'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. Warning in every country for a code that needs a check: "Line 2 has signal code REV, which needs a check before quoting."',
    whatToDo:
      "Check the item status in F&O before you quote this line. Use the replacement item, or ask product support which item to offer.",
    example:
      "A customer asks for a liner plate that is marked REV in F&O. The line gets the warning, and you confirm the current revision before you quote.",
    registryIds: ["gate.signal_review"],
    glossaryIds: [],
    modes: {
      "gate.signal_review": {
        default: "warn",
      },
    },
    source: "goa-knowledge.md part A section 2.2 (Signal codes), backlog M6; pricing/lookup.py",
  },
  {
    id: "media-items",
    title: "Blast media lines",
    status: "active",
    tone: "warning",
    topic: "items",
    checks: "Whether a line is blast media, which means its item group starts with MEDIA_.",
    why: "Media is often shipped differently, so Go Autonomous added no automatic carriage line for it.",
    youSee:
      'Warning in every country: "Line 2 is in the Media item group ..." Where freight goes by order value, the freight in the preview says "Set by hand" too.',
    whatToDo: "Set the carriage for this line by hand, media has no automatic carriage.",
    example:
      "A customer asks for 2 tonnes of steel shot and 3 wear parts. The shot line gets the warning, and you set the carriage yourself.",
    registryIds: ["gate.media"],
    glossaryIds: [],
    modes: {
      "gate.media": {
        default: "warn",
      },
    },
    source:
      "rules/registry.py gate.media; rules/eligibility.py _manual_group; rules/data/manual_item_groups.csv (*, MEDIA_); Check list for other entities.xlsx; Issue Log.xlsx #18",
  },
  {
    id: "service-visit-parts",
    title: "Parts for a service visit",
    status: "active",
    tone: "warning",
    topic: "items",
    checks:
      "Whether a line is a service item rather than a spare part, which means its item group starts with SERV_ or SERVICE. In India the group SI_SERV counts too.",
    why: "Parts for a service visit need a different sales product and sales pool, so Go Autonomous left them to a person.",
    youSee:
      'Warning: "Line 2 is a service item in group ..., not a spare part." This check is off in France.',
    whatToDo: "Handle quotations for service visits by hand.",
    example:
      "After a technician's visit a customer asks for the parts on the visit report and one service item. The service line gets the warning, and you quote the visit by hand.",
    registryIds: ["gate.service_visit"],
    glossaryIds: [],
    modes: {
      "gate.service_visit": {
        default: "warn",
        fr: "off",
      },
    },
    source:
      "rules/registry.py gate.service_visit; rules/data/manual_item_groups.csv (*, SERV_ and SERVICE; in, SI_SERV); modes.yaml fr off; Check list for other entities.xlsx",
  },
  {
    id: "out-of-scope-item-groups",
    title: "Item groups outside parts sales",
    status: "active",
    tone: "warning",
    topic: "items",
    checks:
      "Whether a line is in an item group that your company does not quote as spare parts. Today the list has the Impact groups (IMP_) in the UK and the OEM and SUB groups in Denmark.",
    why: "These groups belong to another business than parts sales, so Go Autonomous handled them by hand.",
    youSee:
      'Warning: "Line 2 is in item group ..., which is outside parts sales." Such lines do not count when I2Q proposes the sales pool.',
    whatToDo: "Check with your manager whether your company quotes this line.",
    example:
      "A UK inquiry has 4 wear parts and 1 line in an Impact item group. The Impact line gets the warning, and the sales pool follows the 4 wear parts.",
    registryIds: ["gate.out_of_scope_group"],
    glossaryIds: [],
    modes: {
      "gate.out_of_scope_group": {
        default: "warn",
      },
    },
    source:
      "rules/registry.py gate.out_of_scope_group; rules/data/manual_item_groups.csv (uk IMP_, dk OEM, dk SUB); rules/header.py _group_counts",
  },
  {
    id: "successor-items",
    title: "Old item numbers and their successors",
    status: "active",
    tone: "warning",
    topic: "items",
    checks:
      "Whether the item asked for has a successor in F&O that replaces it always, or once the old item is out of stock.",
    why: "Go Autonomous sometimes swapped a valid item for its older predecessor, and customers asked to see their own old number on the quotation.",
    youSee:
      'Warning in every country: "Line 3: use 654321, it replaces 123456." When the successor applies only after the old stock is used up, the reason says "once 123456 is out of stock". I2Q keeps the item you asked for on the line; it never swaps it by itself.',
    whatToDo:
      "Pick the successor on the line if it fits the customer's need. Mention the old number in your reply so the customer recognises it.",
    example:
      "A customer asks for an old 9105-series part that is being phased out. The line shows the replacement number. You quote the new item and name the old number in your reply.",
    plannedPart: "Print the old number on the quotation line.",
    registryIds: ["gate.successor"],
    glossaryIds: [],
    modes: {
      "gate.successor": {
        default: "warn",
      },
    },
    source: "goa-knowledge.md part A section 2.2 (Alternatives and successors), backlog M6",
  },
  {
    id: "customer-item-numbers",
    title: "The customer's own item numbers",
    status: "active",
    tone: "info",
    topic: "items",
    checks:
      "Whether the customer used their own part number. I2Q looks it up in the customer item numbers stored in F&O.",
    why: "In Germany about 40 % of orders use the customer's own number, and Go Autonomous's cross-reference covered only about 1 % of our items.",
    youSee:
      'Info: "Matched" when the customer number is known. Otherwise "Needs a check" with the closest items.',
    whatToDo: "Pick the right item. Your pick is stored, so the tool learns it for next time.",
    example:
      'A customer asks for "KD-4471 impeller". I2Q finds our item from the stored customer number.',
    plannedPart:
      "About 2,500 more links from customer text to our item, from the old order reading tool.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.2 (Customer item numbers), backlog S1",
  },
  {
    id: "reading-photos-and-scans",
    title: "Reading photos and scans",
    status: "planned",
    tone: "info",
    topic: "items",
    checks:
      "Photos and scanned pages attached to an inquiry: machine nameplates, part labels, parts lists with marked rows, drawings and scanned forms. I2Q reads part numbers, quantities, drawing numbers and machine details from them. It reads each picture twice, once whole and once in parts, and compares the two readings.",
    why: "About one inquiry in ten comes with a photo or a scan, and many of them have no typed line at all. Without reading the picture, such an inquiry gets no draft lines.",
    youSee:
      'Info: the panel "From the photo" on the case, above "Attachments read". Each picture shows a small preview, what was read, how sure the reading is and which line it fed. A line read from a photo is tagged "Read from a photo".',
    whatToDo:
      'Click "Show where" to see the part of the picture a value was read in. Compare it, then accept, correct or remove the line.',
    example:
      "A customer sends a photo of a worn part with a stamped number. Both readings find the same number and it is in the catalogue. The line is suggested, never accepted, and waits for your decision.",
    registryIds: [],
    glossaryIds: [],
    source: "docs/vision-ocr.md sections 2, 3 and 4.1 (on for every company since 14 October 2026)",
  },
  {
    id: "line-from-a-picture",
    title: "A line from a picture always needs your decision",
    status: "planned",
    tone: "warning",
    topic: "items",
    checks: "Every line whose item number was read from a photo or a scanned page.",
    why: "A number read from a picture can be wrong by one character and still be a real part number. So a line from a picture is never accepted on its own and the F&O draft waits for your decision.",
    youSee:
      'Warning: the line is suggested or needs your pick, never matched. It is tagged "Read from a photo" or "From a scan", and the prepared quotation waits until you have decided the line.',
    whatToDo: "Compare the number with the picture, then accept, correct or remove the line.",
    example:
      "The photo reads 10008503 for a valve. That number is not in the catalogue, but 10085033 has the same name as the row. I2Q suggests 10085033, never as matched. You check the picture and confirm or pick the item.",
    registryIds: [],
    glossaryIds: [],
    source:
      "docs/vision-ocr.md section 3.2 and 3.4 (reason code photo_read; scan_read for scanned pages); fo/preconditions.py",
  },
  {
    id: "agreed-read-once-unclear",
    title: "Agreed, read once, unclear",
    status: "planned",
    tone: "info",
    topic: "items",
    checks:
      "How sure the reading of each value from a picture is. Agreed: both readings found the same characters. Read once: only one reading found it. Unclear: at least one character could not be read. I2Q only looks for an item with the same name.",
    why: "The model cannot say how sure it is in a way that can be trusted. So I2Q compares two independent readings and decides by that, the same way for every case.",
    youSee:
      'Info: the column "Reading" of the panel "From the photo", with the confidence. Agreed is 80%, read once 40%, unclear 10%. A line from a picture never counts as more than 60%, below every limit for accepting a line.',
    whatToDo:
      "Check a value that was read once or is unclear against the picture before you use it. An agreed value can still be wrong, so compare it too.",
    example:
      'A nameplate shows serial 12345. Both readings agree, so it shows "Agreed (two readings)". A stamped number with a worn digit reads 98?6-112, shows "Unclear" and is never matched on its own.',
    registryIds: [],
    glossaryIds: [],
    source: "docs/vision-ocr.md section 2.4 (statuses and confidence)",
  },
  {
    id: "pictures-not-read",
    title: "Why some pictures are not read",
    status: "planned",
    tone: "info",
    topic: "mail-and-case",
    checks:
      "Which pictures are read: attached pictures, pictures in the mail body, and pictures inside an attached mail, sheet or text document (14 October 2026). Logos, signature images and banners are skipped. A picture that was already read, for example in a forwarded mail or a reply, is not sent to the model again: the earlier reading is used. Phone photos (HEIC) are read. At most six pictures per mail are read; a picture over the limit is listed with the reason.",
    why: "Most pictures in a mail are logos and signatures. Reading them costs time and money and gives nothing. A picture repeated in a reply is read once, and the earlier reading is used again.",
    youSee:
      'Info: "Attachments read" names the reason for every picture that was not read, for example "Logo, banner or signature picture, not read". A picture you should know about is also listed under "From the photo" with the reason.',
    whatToDo:
      "Open the file when the picture matters. A case read before 14 October 2026 can still say a HEIC photo was not read; I2Q reads it again.",
    example:
      "A customer sends three photos from a phone in HEIC format. I2Q reads them, and each row read from them waits for your decision.",
    registryIds: [],
    glossaryIds: [],
    source: "docs/vision-ocr.md section 1.2 (skip rules) and 2.6 (limits per mail)",
  },
  {
    id: "machine-details-from-a-nameplate",
    title: "Machine details from a nameplate",
    status: "planned",
    tone: "info",
    topic: "items",
    checks: "Make, type, serial number and year read from a machine nameplate.",
    why: "The serial number and the machine type tell sales which machine the parts are for. They are kept so that the installed base can be linked later.",
    youSee:
      "Info: make, type, serial number and year are shown on the case. They do not change the matching yet.",
    whatToDo:
      "Use them to check that the requested parts fit the machine. A nameplate never gives a draft line.",
    example:
      'A photo of a nameplate shows type 2110 MK II, serial 12345, year 1998. The panel lists them with "Machine details (not used for matching yet)".',
    registryIds: [],
    glossaryIds: [],
    source: "docs/vision-ocr.md section 3.3 (machine context)",
  },
  {
    id: "never-zero-price",
    title: "Never a zero price",
    status: "active",
    tone: "blocked",
    topic: "price",
    checks:
      "Every line has a price above zero from the customer's price list in F&O (F&O calls it a trade agreement). The price must be valid today, and all lines must be in the customer's currency.",
    why: "A line with no price or a zero price would go out as free, so Go Autonomous sent these lines to manual handling.",
    youSee:
      'Blocked in every country. The line shows "No price", and the reason says what is missing. For example: "Line 2 has no price and no lead time because no trade agreement applies." The other lines still show.',
    whatToDo:
      "Ask for a price. A zero price is never quoted. Ask as your country does today, for example a price request to purchasing or the supplying plant.",
    example:
      "A customer asks for 3 items. One has no price list entry. The quotation preview is blocked for that line until the price exists in F&O.",
    registryIds: ["pricing.no_price"],
    glossaryIds: [],
    modes: {
      "pricing.no_price": {
        default: "block",
      },
    },
    source:
      "goa-knowledge.md part A section 2.3 (No price / zero price); fo/preconditions.py check_prices",
  },
  {
    id: "price-age",
    title: "Expired and old prices",
    status: "active",
    tone: "blocked",
    topic: "price",
    checks:
      "Whether the price for the item has expired, and whether the price row it comes from is more than 18 months old.",
    why: "In the UK a ship date of today in F&O meant the price had expired, and Go Autonomous should not have used it.",
    youSee:
      'Blocked: the line shows "Price expired", and the reason says "The price of line 2 has expired." Warning in every country for an old price: "The price of line 2 is valid since 2025-01-15, more than 18 months ago."',
    whatToDo:
      "For an expired price, ask for an updated one before you quote. For an old price, ask for the item to be re-costed before the quotation goes out.",
    example:
      "A price list entry ended on 30 September. On 9 October I2Q holds the line and says so. Another line has a price from March 2025 and gets the re-costing warning.",
    registryIds: ["pricing.no_price", "pricing.price_age"],
    glossaryIds: [],
    modes: {
      "pricing.no_price": {
        default: "block",
      },
      "pricing.price_age": {
        default: "warn",
      },
    },
    source: "goa-knowledge.md part A section 2.3 (Expired / stale price), backlog S4",
  },
  {
    id: "last-quotation-history",
    title: "What the customer was quoted before",
    status: "active",
    tone: "info",
    topic: "price",
    checks:
      "For each line, the last quotation in F&O, made before the inquiry arrived, that had the same item for the same customer in the same company: price, quantity, date and whether it was won or lost. The header shows the sales unit, sales responsible, delivery terms, mode of delivery and price group of the customer's latest quotations where the customer account has none.",
    why: "Sales look up the last quotation by hand before they quote a known customer again, and a price far away from it is the first thing a customer asks about.",
    youSee:
      'Info: under the line price, "Last quoted SQ... on 2026-09-30: 4 pcs at 100.00 GBP" with won or lost. A note when the price differs by more than 15 % from the last quoted price in the same currency, for example "The price of line 2 is 20 % above the customer\'s last quotation SQ... of 2026-09-30." On the header, "Last quotation SQ..., date" next to each value taken from it.',
    whatToDo:
      "Check the price and the header values against the last quotation before the quotation goes out. Nothing is accepted or changed because of it: the sales taker and the sales pool of the last quotation are shown, never used.",
    example:
      "A customer asks again for 4 wear plates. The last quotation in September had them at 100.00 GBP and was won. The price list now says 120.00 GBP, so the line shows a note that the price is 20 % above the last quotation.",
    registryIds: ["pricing.vs_last_quote"],
    glossaryIds: [],
    modes: {
      "pricing.vs_last_quote": {
        default: "info",
      },
    },
    source: "docs/oa-quote-data.md section 5 b and c (F&O quotations via OmniAnalytics)",
  },
  {
    id: "contribution-ratio",
    title: "Margin check (contribution ratio)",
    status: "active",
    tone: "blocked",
    topic: "price",
    checks:
      "Where F&O has a cost for the item, how much of the price is left after cost (the contribution ratio). It is compared with the margin limit of your area.",
    why: "Go Autonomous stopped low-margin quotations, and the countries asked for a clear step that a manager can release instead.",
    youSee:
      'Blocked until a manager approves: the line shows "Margin: needs approval". In India it is a warning instead, "Check the price before the quotation goes out.", and nothing is held. If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held in every country. Only colleagues with margin rights see the figures.',
    whatToDo:
      'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again. If no cost is known, create the quotation in F&O as today.',
    example:
      'A large quantity of a cheap part has a low price group price. The line shows "Margin: needs approval" and your manager approves it.',
    registryIds: ["pricing.margin"],
    glossaryIds: [],
    modes: {
      "pricing.margin": {
        default: "block",
        in: "warn",
      },
    },
    source:
      "goa-knowledge.md part A section 2.3 (Contribution ratio gate), backlog M12; pricing/thresholds.py",
  },
  {
    id: "freight",
    title: "Freight",
    status: "active",
    tone: "warning",
    topic: "freight",
    checks:
      "Whether the quotation needs a freight line. Each company follows one of three models. By order value bands: the UK in GBP, France and Spain in EUR, Czechia in CZK. In the item price: India. No freight on the quotation: Denmark, North America, Mexico and Germany.",
    why: "France stopped using Go Autonomous for months because customers with a no-freight agreement still got a freight charge.",
    youSee:
      'The quotation preview has a "Freight" block. With bands it says "Computed", with the amount and the band. A note says "Freight of 80 GBP is proposed for an order value of 500.01 to 2000 GBP." In France this is a warning. A warning "Set by hand" shows above the last band, for a Media line or for another currency than the bands.',
    whatToDo:
      "Check the freight line before the quotation goes out. Change it for light or bulky parts, and remove it if the customer has a no-freight agreement.",
    example:
      "A French customer asks for parts worth EUR 1,200. The preview proposes 75 EUR freight and warns you to check whether this customer pays freight.",
    plannedPart:
      "Lists of customers who pay no freight. Today no company has one, so France gets a warning on every proposed freight line.",
    registryIds: ["pricing.freight"],
    glossaryIds: [],
    modes: {
      "pricing.freight": {
        default: "warn",
      },
    },
    source:
      "goa-knowledge.md part A section 1 point 5, section 2.5 (Freight tiers), section 9.3 (three models), backlog M5",
  },
  {
    id: "lead-time-warnings",
    title: "Lead time warnings",
    status: "active",
    tone: "warning",
    topic: "price",
    checks:
      "Whether the lead time from F&O is longer than 99 days, and whether the line can ship only after the date the customer asked for. The requested date is the line's own date, else the date for the whole inquiry.",
    why: "Sales asked for warnings, not blocks, and agreed to trust the F&O lead time instead of stock rules in the tool.",
    youSee:
      'Warning in every country: "Line 2 has a lead time of 120 days." or "Line 2 can ship on 2026-12-02, after the requested date 2026-11-15." I2Q does not have the F&O lead times yet, so you rarely see these warnings today. The check for a price without a lead time is off in every country.',
    whatToDo:
      "Tell the customer the earliest ship date or check a faster source. Check long lead times with purchasing before the quotation goes out.",
    example:
      "The customer needs the part by 1 November. F&O says 6 weeks. I2Q warns you so you can promise a realistic date.",
    plannedPart:
      "Lead times from F&O, so these warnings show reliably. Then the check for a price without a lead time can be switched on again.",
    registryIds: ["pricing.lead_time", "pricing.after_requested", "pricing.ship_today"],
    glossaryIds: [],
    modes: {
      "pricing.lead_time": {
        default: "warn",
      },
      "pricing.after_requested": {
        default: "warn",
      },
      "pricing.ship_today": {
        default: "off",
      },
    },
    source: "goa-knowledge.md part A section 2.3 (Lead time), backlog S4",
  },
  {
    id: "minimum-order-value",
    title: "Below the minimum order value",
    status: "planned",
    tone: "info",
    topic: "price",
    checks: "Planned: whether the quotation is below your country's minimum order value.",
    why: "Small orders cost more to handle than they earn, and in the UK raising the minimum charge earned about GBP 80,000 a year.",
    youSee: 'Info (planned): "Below the minimum order value".',
    whatToDo: "Add the minimum charge your country uses, or suggest more items to the customer.",
    example:
      "A UK customer asks for one gasket worth GBP 40. I2Q notes it is under the GBP 225 minimum.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 9.1 (Minimum order value), backlog S16",
  },
  {
    id: "large-quotation",
    title: "Very large quotations",
    status: "active",
    tone: "warning",
    topic: "price",
    checks:
      "Whether the quotation total is above the approval limit: 50,000 EUR or 50,000 GBP. It runs only when every line is priced in one currency. Other currencies have no limit yet.",
    why: "Go Autonomous sent quotations above 50,000 EUR or GBP to manual handling, because larger amounts need approval.",
    youSee:
      'Warning: "The quotation total of 62,000.00 EUR is above the approval limit of 50,000.00 EUR." In a currency without a limit you see a note: "No approval limit is set for USD, so the total is not checked."',
    whatToDo: "Ask your manager to approve this quotation before it goes out.",
    example:
      "A customer asks for a full set of wear parts worth EUR 62,000. The case shows the approval warning, and your manager approves before you send.",
    registryIds: ["gate.doa_limit"],
    glossaryIds: [],
    modes: {
      "gate.doa_limit": {
        default: "warn",
      },
    },
    source: "goa-knowledge.md part A section 2.3 (Delegation of authority)",
  },
  {
    id: "quotation-header",
    title: "How the quotation header is filled",
    status: "active",
    tone: "warning",
    topic: "quotation",
    checks:
      "The sales pool and three sales fields (division, business type, sales product) come from the item group with the most lines. Item groups outside parts sales do not count. Sales unit, sales responsible, sales taker, payment terms, delivery terms and mode of delivery should come from the customer.",
    why: "In Go Autonomous a wrong default pool had to be changed by hand on every UK quotation, and leavers in a sales unit put the wrong name on quotations.",
    youSee:
      'The quotation preview shows the proposed sales pool and sales fields. Warning when no pool can be proposed, for example "Item group ... has no sales pool for this entity." or when two item groups tie. A note lists the customer fields that are not available yet.',
    whatToDo:
      "Check the header in F&O when you create the quotation. Pick the sales pool and the CRM fields in F&O where I2Q proposes none.",
    example:
      "A quotation has 3 lines of shot blast parts and 1 line of moulding parts. The preview proposes the shot blast pool.",
    plannedPart:
      "Sales unit, sales responsible, sales taker, payment terms, delivery terms and mode of delivery from the customer account.",
    registryIds: ["header.pool", "header.not_available", "header.from_last_quotation"],
    glossaryIds: [],
    modes: {
      "header.pool": {
        default: "warn",
      },
      "header.not_available": {
        default: "info",
      },
      "header.from_last_quotation": {
        default: "info",
      },
    },
    source: "goa-knowledge.md part A section 1 point 4, section 2.4, backlog M1, M2",
  },
  {
    id: "customer-reference-fields",
    title: "Customer reference and requisition",
    status: "active",
    tone: "info",
    topic: "quotation",
    checks:
      "On a quotation, the customer reference holds the customer's inquiry number from the mail, up to 30 characters. The requisition field stays empty, because it is for the purchase order number on an order.",
    why: "Go Autonomous and the countries filled these fields in different ways, so quotations could not be found again.",
    youSee:
      'Info: "Customer reference" on the quotation preview. When the mail names no inquiry number, the reference stays empty.',
    whatToDo:
      "Check the reference, especially in Germany and the US where the fields are used differently.",
    example:
      'The customer writes "Our RFQ 4500-221". The prepared quotation shows 4500-221 as the customer reference.',
    plannedPart: "The German use of the two fields, see the Germany guide.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.5 (Customer requisition / reference), backlog M8",
  },
  {
    id: "currency",
    title: "One currency, from the customer",
    status: "active",
    tone: "blocked",
    topic: "quotation",
    checks:
      "The currency comes from the customer account, and all lines must be in the same currency.",
    why: "Go Autonomous sent a symbol such as £ instead of the currency code, which F&O did not accept.",
    youSee: 'Blocked: "The lines are priced in several currencies" or "A price has no currency".',
    whatToDo: "Check the customer's price group in F&O.",
    example:
      "One line is priced in EUR and one in GBP. The prepared quotation is blocked until it is clear which applies.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.3 (Currency); fo/preconditions.py check_prices",
  },
  {
    id: "translation",
    title: "Translation and reply language",
    status: "active",
    tone: "info",
    topic: "quotation",
    checks:
      "A mail in another language is shown translated, and the reply draft is written in the language the desk uses for that customer: the customer's own language where the desk answers in it, else English. Denmark answers Danish and German customers in their language and every other customer, Norway and Sweden included, in English.",
    why: "Colleagues answer customers from many countries, for example France answers Danish customers in French.",
    youSee:
      'Info: "Translated from Spanish" above the text, with "Show original". If a number is missing in the translation, the pane says so. The quotation preview shows the document language: the customer\'s, else your company\'s, marked "(company default)".',
    whatToDo:
      "Use the original for quantities, prices and item numbers. Read the reply draft before you use it.",
    example:
      "A Turkish customer writes in Turkish. You read it in English, and the reply draft is in Turkish.",
    registryIds: [],
    glossaryIds: [],
    source:
      'goa-knowledge.md part A section 2.5 (Reply language, Quote document language); help topic "Mails in another language"',
  },
  {
    id: "quotation-validity",
    title: "How long the quotation is valid",
    status: "planned",
    tone: "info",
    topic: "quotation",
    checks:
      "Planned: the validity of the quotation, the same in F&O and in the reply text, from your country's rule.",
    why: "Reply templates said 30 days while some F&O quotations ran for 90 days, which confused customers.",
    youSee: 'Info (planned): "Valid until" on the quotation preview and in the reply draft.',
    whatToDo: "Check the date before you send.",
    example: 'A UK quotation is valid for one month, so the reply says "valid until 9 November".',
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 2.5 (Validity / expiry), backlog S3",
  },
  {
    id: "manual-cases",
    title: "Cases that always need a person",
    status: "planned",
    tone: "warning",
    topic: "items",
    checks:
      "Whether a request is only a picture without a part number, or one mail holds several separate inquiry files.",
    why: "Go Autonomous quoted pictures and merged several inquiries, which led to wrong parts and returns.",
    youSee:
      'Planned: a warning "Check by hand" with the reason. Today, lines that could not be read show "Not evaluated, check by hand".',
    whatToDo: "Ask the customer for the Norican part number, or split the inquiries yourself.",
    example:
      "A customer sends a photo of a worn part. I2Q asks you to confirm the part number with the customer first.",
    registryIds: [],
    glossaryIds: [],
    source: "goa-knowledge.md part A section 9.1 (Pictures #16, several RFQs #19)",
  },
  {
    id: "master-data-age",
    title: "Up-to-date customer and item data",
    status: "active",
    tone: "blocked",
    topic: "mail-and-case",
    checks: "Whether the copy of F&O data that I2Q uses was refreshed recently.",
    why: "Go Autonomous worked on data that was a day old or incomplete, so new contacts and items were missing.",
    youSee: 'Blocked: "Master data is older than its refresh limit" on the prepared quotation.',
    whatToDo:
      "Nothing on your side. Work in F&O directly, and tell IT if it lasts more than a day.",
    example:
      "The overnight refresh failed. Every prepared quotation shows the block until the data is fresh again.",
    registryIds: [],
    glossaryIds: [],
    source:
      "goa-knowledge.md part A section 6.5, section 9.1; fo/preconditions.py check_master_data_fresh",
  },
  {
    id: "nothing-is-sent",
    title: "Nothing is sent, nothing is written",
    status: "active",
    tone: "info",
    topic: "mail-and-case",
    checks:
      "I2Q never sends a mail, never makes an Outlook draft and never creates the quotation in F&O. Orders, order confirmations and quote-to-order are out of scope.",
    why: "In Go Autonomous a person always sent, and that was one of the things that worked.",
    youSee:
      'Info: the shadow mode badge on every page. "Create quotation in the order system" is switched off.',
    whatToDo:
      "Copy the reply text into Outlook, create the quotation in F&O and send it yourself, as today.",
    example:
      'You click "Approve for the order system". This records your approval in I2Q only; the quotation is still created by you in F&O.',
    registryIds: [],
    glossaryIds: [],
    source: "docs/scope.md; goa-knowledge.md part A section 6.4, section 7",
  },
  {
    id: "rule-check-failed",
    title: "A rule check that could not run",
    status: "active",
    tone: "blocked",
    topic: "mail-and-case",
    checks:
      "Whether every rule check could run for the case. When one fails because of a technical error, I2Q holds the case instead of letting it pass.",
    why: "A check that fails without a word could let a competitor or a sanctioned sender through, so I2Q holds the case instead.",
    youSee:
      'Blocked in every country: "A rule check could not run for this case, so it is held." The reasons of the other checks still show.',
    whatToDo:
      'Check the case by hand and tell IT if this happens again. You can use "Give feedback" on the case for that.',
    example:
      "The price checks fail because of a data error. The case is held with this reason, and you prepare the quotation in F&O as today.",
    registryIds: ["gate.evaluation_failed"],
    glossaryIds: [],
    modes: {
      "gate.evaluation_failed": {
        default: "block",
      },
    },
    source:
      "rules/registry.py gate.evaluation_failed; rules/evaluate.py (fail closed); fo/preconditions.py check_entity_rules",
  },
  {
    id: "master-data-checks",
    title: "Master data checks on the finished draft",
    status: "active",
    tone: "warning",
    topic: "quotation",
    checks:
      "Eight checks of the finished draft against the customer and item data of F&O. The item is in the item master. The item is set up for your company. The item is not blocked for sales. The line has a valid list price on the day the mail arrived. The quantity is above zero. The customer account is not marked do not use. The currency in the mail is the sales currency of the customer. The inquiry reference was not quoted before.",
    why: "F&O refuses a quotation with such an item or account, and you see that only when you create the quotation. These checks say it while you read the draft.",
    youSee:
      'Warning: the reason stands under "Checks" on the case, marked "Check", with the line it is about. The currency check is a note. Nothing is held. IT turns these checks on for one company at a time. Until then you see no such reason.',
    whatToDo:
      "Read the reason and the line it names. Correct the item, the quantity or the account. If the item is right, ask the master data team to release it.",
    example:
      "A line has an item that is not set up for your company. The case lists the reason for that line. You pick the item of your company, or you ask the master data team.",
    registryIds: [
      "preflight.item_exists",
      "preflight.item_released",
      "preflight.item_blocked",
      "preflight.price_available",
      "preflight.quantity",
      "preflight.customer_valid",
      "preflight.currency",
      "preflight.duplicate_reference",
    ],
    glossaryIds: [],
    modes: {
      "preflight.item_exists": {
        default: "warn",
      },
      "preflight.item_released": {
        default: "warn",
      },
      "preflight.item_blocked": {
        default: "warn",
      },
      "preflight.price_available": {
        default: "warn",
      },
      "preflight.quantity": {
        default: "warn",
      },
      "preflight.customer_valid": {
        default: "warn",
      },
      "preflight.currency": {
        default: "info",
      },
      "preflight.duplicate_reference": {
        default: "warn",
      },
    },
    source:
      "rules/registry.py preflight.*; rules/preflight_stage.py; preflight/live.py (switch RFQ_PREFLIGHT, default off); docs/agentic-investigator.md section 9",
  },
];

/** How I2Q works, in six steps. */
export const HOW_IT_WORKS: HowItWorks = {
  title: "How I2Q works in 6 steps",
  summary:
    "I2Q turns a customer inquiry into a draft quotation for you to check. It runs in shadow mode: it sends nothing and creates nothing in F&O. You always decide and you always send.",
  steps: [
    {
      title: "A mail arrives",
      text: "A customer, an agent, a portal or a colleague writes to a shared mailbox. I2Q reads the newest message and the attached files within a few minutes. Photos and scanned pages are read too, for every company.",
    },
    {
      title: "Is it an inquiry?",
      text: 'Only a mail that asks for a price or a quotation goes on. A purchase order goes to "Purchase orders (parked)", everything else to "Not an inquiry".',
    },
    {
      title: "Who is the customer?",
      text: "I2Q looks for the customer account in F&O. It is either certain or a suggestion that waits for your click.",
    },
    {
      title: "Lines and items",
      text: "Each line the customer asks for is matched to one of our items. Where the match is not sure, you pick.",
    },
    {
      title: "Prices and checks",
      text: 'I2Q fills in the price from the customer\'s price list in F&O and runs the checks in this glossary. The reasons show under "Why this case needs attention", each with what to do.',
    },
    {
      title: "You decide and send",
      text: "I2Q prepares a reply text and a preview of the quotation. You check them, answer the customer and create the quotation in F&O as today.",
    },
  ],
};

/** What info, warning and blocked mean. */
export const WARNING_OR_BLOCKED: WarningOrBlocked = {
  title: "Warning or blocked: what is the difference",
  text: [
    'Info means: good to know, nothing to do. On the case it is labelled "Note" and listed under "Other notes". It never shows in the queue.',
    'A warning means: I2Q filled everything in, but something looks unusual. On the case it is labelled "Check", and the queue shows the first reason. Read it, decide, and carry on.',
    'Blocked means: the case is held until the reason is dealt with. On the case it is labelled "Holds the case", under "Held until these are dealt with". The prepared quotation cannot be approved until then.',
    "Even when something is blocked you can still answer the customer as you do today. I2Q never sends anything, so a block only stops the I2Q draft, not your work.",
    "Norican prefers warnings. In Go Autonomous people stopped using the tool when it could not tell them why a quotation failed. A rule is a block only where a wrong quotation would cost real money or trouble.",
    'Each company has its own setting per rule: block, warning, note or off. IT sets it in the rule files, not on the screen. Managers and admins see the setting in the tab "Live settings". To change it, ask the I2Q owner of your country.',
  ],
  example:
    'Line 3 has no price in F&O. The prepared quotation shows "No price was found for 123456" and cannot be approved. The reply draft still lists the other lines, and you ask purchasing for the price as today.',
};
